import { ListSMSPhoneNumbersIO } from '../types';
import { usePhoneNumbersV1Queries } from './use-phone-numbers-v1-queries';
import { UsePhoneNumbersV1QueryEndpointArgs, usePhoneNumbersV1Query } from './use-phone-numbers-v1-query';

/**
 * A hook that returns a query for the `ListSMSPhoneNumbers` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListSMSPhoneNumbersQuery = <E = unknown, D = ListSMSPhoneNumbersIO['output']>(
  args: UsePhoneNumbersV1QueryEndpointArgs<'ListSMSPhoneNumbers', E, D>
) =>
  usePhoneNumbersV1Query({
    endpointName: 'ListSMSPhoneNumbers',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `ListSMSPhoneNumbers` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListSMSPhoneNumbersQueries = <E = unknown, D = ListSMSPhoneNumbersIO['output']>(
  argsArr: UsePhoneNumbersV1QueryEndpointArgs<'ListSMSPhoneNumbers', E, D>[]
) =>
  usePhoneNumbersV1Queries(
    argsArr.map((args) => ({
      endpointName: 'ListSMSPhoneNumbers',
      ...args,
    }))
  );
